import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { autovoteApi } from '../services/api';
import hiveService from '../services/hive';
import { useSettings } from '../context/SettingsContext';
import '../pages/Autovoting.css';

function AutovotingFollowing() {
  const { user, isAuthenticated, loading } = useAuth();
  const { voteDefaultWeight } = useSettings();
  
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);

  // User Search State
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form Fields
  const defaultWeight = voteDefaultWeight ? parseInt(voteDefaultWeight, 10) : 100;

  const [isScaleWeight, setIsScaleWeight] = useState(true);
  const [voteArticles, setVoteArticles] = useState(true);
  const [articleWaitTime, setArticleWaitTime] = useState(0);
  const [articleWeight, setArticleWeight] = useState(defaultWeight);
  const [articleDailyLimit, setArticleDailyLimit] = useState(0);
  
  const [voteShortContent, setVoteShortContent] = useState(false);
  const [shortPostWaitTime, setShortPostWaitTime] = useState(0);
  const [shortPostWeight, setShortPostWeight] = useState(defaultWeight);
  const [shortPostDailyLimit, setShortPostDailyLimit] = useState(0);
  
  const [voteComments, setVoteComments] = useState(false);
  const [commentWaitTime, setCommentWaitTime] = useState(0);
  const [commentWeight, setCommentWeight] = useState(defaultWeight);
  const [commentDailyLimit, setCommentDailyLimit] = useState(0);

  const [savingRule, setSavingRule] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    addRule: false,
    rules: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const loadRules = useCallback(async () => {
    if (!user?.username) return;
    setLoadingRules(true);
    try {
      const data = await autovoteApi.getRules(user.username);
      setRules((data.rules || []).filter(r => r.target_voter));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRules(false);
    }
  }, [user?.username]);

  useEffect(() => {
    if (!user?.username) return;
    loadRules();
  }, [user?.username, loadRules]);

  // --- User Search Handlers ---
  useEffect(() => {
    const q = searchValue.trim().toLowerCase();
    if (q.length < 3) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);

    const timeoutId = setTimeout(async () => {
      try {
        const matches = await hiveService.searchAccounts(q, 5);
        if (cancelled) return;
        setSearchResults(matches);
      } catch (_err) {
        if (cancelled) return;
        setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchValue]);

  const selectUser = (username) => {
    setSelectedUser(username);
    setSearchValue('');
    setSearchResults([]);
  };

  const handleResetForm = () => {
    setSelectedUser(null);
    setSearchValue('');
    setArticleWaitTime(0);
    setArticleWeight(defaultWeight);
    setArticleDailyLimit(0);
    setVoteArticles(true);
    setVoteShortContent(false);
    setShortPostWaitTime(0);
    setShortPostWeight(defaultWeight);
    setShortPostDailyLimit(0);
    setVoteComments(false);
    setCommentWaitTime(0);
    setCommentWeight(defaultWeight);
    setCommentDailyLimit(0);
    setIsScaleWeight(true);
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert("Please select a user to follow.");
      return;
    }

    setSavingRule(true);
    try {
      const payload = {
        target_voter: selectedUser,
        is_scale_weight: isScaleWeight,
        article_wait_time: parseInt(articleWaitTime, 10) || 0,
        article_weight: parseInt(articleWeight, 10) || null,
        article_daily_limit: parseInt(articleDailyLimit, 10) || null,
        vote_articles: voteArticles,
        vote_short_content: voteShortContent,
        short_post_wait_time: parseInt(shortPostWaitTime, 10) || 0,
        short_post_weight: parseInt(shortPostWeight, 10) || null,
        short_post_daily_limit: parseInt(shortPostDailyLimit, 10) || null,
        vote_comments: voteComments,
        comment_wait_time: parseInt(commentWaitTime, 10) || 0,
        comment_weight: parseInt(commentWeight, 10) || null,
        comment_daily_limit: parseInt(commentDailyLimit, 10) || null
      };

      await autovoteApi.saveRule(user.username, payload);
      await loadRules();
      
      handleResetForm();
      setExpandedSections({ addRule: false });
    } catch (err) {
      alert("Failed to save rule: " + (err.response?.data?.error || err.message));
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this autovote rule?")) return;
    try {
      await autovoteApi.deleteRule(user.username, ruleId);
      await loadRules();
    } catch (err) {
      alert("Failed to delete rule.");
    }
  };

  const handleEditRule = (rule) => {
    setSelectedUser(rule.target_voter);
    setIsScaleWeight(rule.is_scale_weight === true);
    setVoteArticles(rule.vote_articles);
    setArticleWaitTime(rule.article_wait_time);
    setArticleWeight(rule.article_weight === null ? defaultWeight : rule.article_weight);
    setArticleDailyLimit(rule.article_daily_limit || 0);
    
    setVoteShortContent(rule.vote_short_content);
    setShortPostWaitTime(rule.short_post_wait_time);
    setShortPostWeight(rule.short_post_weight === null ? defaultWeight : rule.short_post_weight);
    setShortPostDailyLimit(rule.short_post_daily_limit || 0);
    
    setVoteComments(rule.vote_comments);
    setCommentWaitTime(rule.comment_wait_time);
    setCommentWeight(rule.comment_weight === null ? defaultWeight : rule.comment_weight);
    setCommentDailyLimit(rule.comment_daily_limit || 0);

    setExpandedSections({ addRule: true, rules: expandedSections.rules });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleRulePause = async (rule) => {
    try {
      const newPausedState = !rule.is_paused;
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_paused: newPausedState } : r));
      await autovoteApi.toggleRulePause(user.username, rule.id, newPausedState);
    } catch (err) {
      await loadRules();
      alert("Failed to toggle pause state.");
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  if (!isAuthenticated) return null;

  return (
    <>
      <div className="av-section">
        <div 
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => toggleSection('addRule')}
        >
          <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Follow a User</h2>
          <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>{expandedSections.addRule ? '▼' : '▶'}</span>
        </div>

        {expandedSections.addRule && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
            <form onSubmit={handleSaveRule}>
              <div className="av-form-group">
                <label>Target User to Follow</label>
                {selectedUser ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-primary)', padding: '8px 12px', borderRadius: 4, border: '1px solid var(--border-color)', width: 'fit-content' }}>
                    <img src={`https://images.hive.blog/u/${selectedUser}/avatar/small`} alt={selectedUser} style={{ width: 24, height: 24, borderRadius: '50%' }} onError={(e) => { e.target.src = '/default-avatar.png'; }} />
                    <span style={{ fontWeight: 500 }}>@{selectedUser}</span>
                    <button type="button" onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
                  </div>
                ) : (
                  <div className="av-search-container">
                    <input
                      type="text"
                      className="av-form-control"
                      placeholder="Search for a Hive user to follow..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      autoComplete="off"
                    />
                    {searching && <div className="av-search-results"><div style={{ padding: '8px 12px' }}>Searching...</div></div>}
                    {!searching && searchValue.length >= 3 && searchResults.length === 0 && (
                      <div className="av-search-results"><div style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>No matches found.</div></div>
                    )}
                    {!searching && searchResults.length > 0 && (
                      <div className="av-search-results">
                        {searchResults.map(match => (
                          <div key={match} className="av-search-item" onClick={() => selectUser(match)}>
                            <img src={`https://images.hive.blog/u/${match}/avatar/small`} alt={match} className="av-search-avatar" onError={(e) => { e.target.src = '/default-avatar.png'; }} />
                            <span>@{match}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="av-form-group" style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={isScaleWeight} 
                    onChange={(e) => setIsScaleWeight(e.target.checked)}
                  />
                  Scale weight to original vote
                </label>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  If enabled, your vote weight will be a percentage of their vote. For example, if you set weight to 50% and they vote with 50%, your final vote will be 25%.
                </p>
              </div>

              <div className="av-type-settings">
                {/* Articles */}
                <div className="av-type-card">
                  <div className="av-type-header">
                    <label className="av-toggle-label">
                      <input type="checkbox" checked={voteArticles} onChange={e => setVoteArticles(e.target.checked)} />
                      Articles
                    </label>
                  </div>
                  {voteArticles && (
                    <div className="av-type-body">
                      <div className="av-form-group">
                        <label>Wait Time (minutes)</label>
                        <input type="number" className="av-form-control" min="0" value={articleWaitTime} onChange={e => setArticleWaitTime(e.target.value)} />
                      </div>
                      <div className="av-form-group">
                        <label>Weight {isScaleWeight ? '(%)' : '(1-100%)'}</label>
                        <input type="number" className="av-form-control" min="1" max="100" value={articleWeight} onChange={e => setArticleWeight(e.target.value)} />
                      </div>
                      <div className="av-form-group">
                        <label>Daily Limit (0 = no limit)</label>
                        <input type="number" className="av-form-control" min="0" value={articleDailyLimit} onChange={e => setArticleDailyLimit(e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Shorts */}
                <div className="av-type-card">
                  <div className="av-type-header">
                    <label className="av-toggle-label">
                      <input type="checkbox" checked={voteShortContent} onChange={e => setVoteShortContent(e.target.checked)} />
                      Shorts
                    </label>
                  </div>
                  {voteShortContent && (
                    <div className="av-type-body">
                      <div className="av-form-group">
                        <label>Wait Time (minutes)</label>
                        <input type="number" className="av-form-control" min="0" value={shortPostWaitTime} onChange={e => setShortPostWaitTime(e.target.value)} />
                      </div>
                      <div className="av-form-group">
                        <label>Weight {isScaleWeight ? '(%)' : '(1-100%)'}</label>
                        <input type="number" className="av-form-control" min="1" max="100" value={shortPostWeight} onChange={e => setShortPostWeight(e.target.value)} />
                      </div>
                      <div className="av-form-group">
                        <label>Daily Limit (0 = no limit)</label>
                        <input type="number" className="av-form-control" min="0" value={shortPostDailyLimit} onChange={e => setShortPostDailyLimit(e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Comments */}
                <div className="av-type-card">
                  <div className="av-type-header">
                    <label className="av-toggle-label">
                      <input type="checkbox" checked={voteComments} onChange={e => setVoteComments(e.target.checked)} />
                      Comments
                    </label>
                  </div>
                  {voteComments && (
                    <div className="av-type-body">
                      <div className="av-form-group">
                        <label>Wait Time (minutes)</label>
                        <input type="number" className="av-form-control" min="0" value={commentWaitTime} onChange={e => setCommentWaitTime(e.target.value)} />
                      </div>
                      <div className="av-form-group">
                        <label>Weight {isScaleWeight ? '(%)' : '(1-100%)'}</label>
                        <input type="number" className="av-form-control" min="1" max="100" value={commentWeight} onChange={e => setCommentWeight(e.target.value)} />
                      </div>
                      <div className="av-form-group">
                        <label>Daily Limit (0 = no limit)</label>
                        <input type="number" className="av-form-control" min="0" value={commentDailyLimit} onChange={e => setCommentDailyLimit(e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" className="av-btn av-btn-secondary" onClick={handleResetForm} disabled={savingRule}>Cancel</button>
                <button type="submit" className="av-btn av-btn-primary" disabled={savingRule || !selectedUser}>
                  {savingRule ? 'Saving...' : 'Save Rule'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="av-section">
        <div 
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => toggleSection('rules')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Active Following</h2>
            {!loadingRules && rules.length > 0 && (
              <span style={{ background: 'var(--primary-color)', color: '#fff', fontSize: '0.8rem', fontWeight: 600, padding: '2px 8px', borderRadius: '12px' }}>
                {rules.length}
              </span>
            )}
          </div>
          <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>{expandedSections.rules ? '▼' : '▶'}</span>
        </div>

        {expandedSections.rules && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
            {loadingRules ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading followed users...</div>
            ) : rules.length > 0 ? (
              <div className="av-rules-list">
                <table className="av-rules-table">
                  <thead>
                    <tr>
                      <th>Target Voter</th>
                      <th>Types</th>
                      <th>Scaled</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map(rule => (
                      <tr key={rule.id} className={rule.is_paused ? 'rule-paused' : ''}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <img src={`https://images.hive.blog/u/${rule.target_voter}/avatar/small`} alt={rule.target_voter} style={{ width: 24, height: 24, borderRadius: '50%' }} onError={(e) => { e.target.src = '/default-avatar.png'; }} />
                            <span style={{ fontWeight: 500 }}>@{rule.target_voter}</span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {rule.vote_articles && <span className="av-badge">Art</span>}
                            {rule.vote_short_content && <span className="av-badge">Sht</span>}
                            {rule.vote_comments && <span className="av-badge">Com</span>}
                          </div>
                        </td>
                        <td>
                          {rule.is_scale_weight ? 'Yes' : 'No'}
                        </td>
                        <td>
                          <span className={`av-status-badge ${rule.is_paused ? 'paused' : 'active'}`}>
                            {rule.is_paused ? 'Paused' : 'Active'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => handleToggleRulePause(rule)} className="av-btn av-btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                              {rule.is_paused ? 'Resume' : 'Pause'}
                            </button>
                            <button onClick={() => handleEditRule(rule)} className="av-btn av-btn-primary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                              Edit
                            </button>
                            <button onClick={() => handleDeleteRule(rule.id)} className="av-btn av-btn-danger" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="av-empty-state">
                You are not following any users yet.
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default AutovotingFollowing;
